import React from 'react'
import { ExternalLink } from '@/atoms/ExternalLink/ExternalLink'
import { paths } from '@/constants/paths'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

const MAX_RETRIES_BEFORE_SUPPORT_MESSAGE = 3

interface HlsErrorOverlayProps {
  message?: string
  retryCount: number
  onRetry?: () => void
}

/**
 * Error message overlay displayed when a fatal playback error occurs.
 * Shows an error message with an optional retry button.
 * After 3 failed retries, shows an additional message linking to support.
 */
export const HlsErrorOverlay: React.FC<HlsErrorOverlayProps> = ({ message, retryCount, onRetry }) => {
  const { t } = useTranslate('common')

  const handleRetry = () => {
    onRetry?.()
  }

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80">
      <p className="photon-body-md mb-4 max-w-md px-4 text-center text-white">
        {message || t('videoPlaybackError', 'An error occurred during video playback. Please try again.')}
      </p>
      {retryCount >= MAX_RETRIES_BEFORE_SUPPORT_MESSAGE && (
        <p className="photon-body-sm mb-4 max-w-md px-4 text-center text-white/80">
          <Translate t={t} i18nKey="videoPlaybackContactSupport">
            Please{' '}
            <ExternalLink href={paths.support.contact} className="underline hover:text-white">
              contact support
            </ExternalLink>{' '}
            if this issue persists.
          </Translate>
        </p>
      )}
      {onRetry && (
        <button
          type="button"
          className="photon-label-md rounded-lg bg-white px-6 py-2 text-core-gray-950 transition-colors hover:bg-white/90"
          onClick={handleRetry}
        >
          {t('retry', 'Retry')}
        </button>
      )}
    </div>
  )
}
